import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { Bot, X } from "lucide-react"
import { Dialog } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

type Props = {
  open: boolean
  onClose: () => void
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")

export function NewAgentModal({ open, onClose }: Props) {
  const [name, setName]               = useState("")
  const [slug, setSlug]               = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [description, setDescription] = useState("")
  const [submitting, setSubmitting]   = useState(false)

  const submitDisabled = useMemo(() => {
    if (submitting) return true
    if (!name.trim() || !slug.trim()) return true
    if (description.trim().length < 10) return true
    return false
  }, [submitting, name, slug, description])

  function onNameChange(v: string) {
    setName(v)
    if (!slugTouched) setSlug(slugify(v))
  }

  function onSlugChange(v: string) {
    setSlug(slugify(v))
    setSlugTouched(true)
  }

  function reset() {
    setName("")
    setSlug("")
    setSlugTouched(false)
    setDescription("")
    setSubmitting(false)
  }

  function close() {
    if (submitting) return
    reset()
    onClose()
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitDisabled) return
    setSubmitting(true)
    router.post(
      "/agents",
      { agent: { name, key: slug, description } },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: () => {
          reset()
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={close} size="md">
      <form onSubmit={submit}>
        {/* ───── Header ───── */}
        <header className="flex items-start gap-4 border-b border-border px-7 py-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-control border border-brand-red/30 bg-brand-redSoft text-brand-red">
            <Bot size={20} strokeWidth={1.7} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold">New agent</h2>
            <p className="mt-0.5 text-sm text-app-muted">
              You'll land on the Edit page to wire skills, voice, and the AI model.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="-mr-2 -mt-1 rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text"
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        {/* ───── Body ───── */}
        <div className="space-y-5 px-7 py-6">
          <Section
            title="Name"
            hint="Shown in the portal and in chat."
          >
            <Input
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Concierge"
              required
              autoFocus
            />
          </Section>

          <Section
            title="Slug"
            hint="snake_case key, unique per company. Auto-derived from Name; editable."
          >
            <Input
              value={slug}
              onChange={(e) => onSlugChange(e.target.value)}
              placeholder="concierge"
              required
              className="font-mono"
            />
          </Section>

          <Section
            title="Describe what this agent should do"
            hint="One paragraph. Becomes the agent's role prompt — you can refine it on the Edit page."
          >
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="You are Concierge, the support agent for Acme. Help users with billing questions, plan changes, and refund requests. Keep replies short and friendly. Escalate anything you can't answer."
            />
          </Section>
        </div>

        {/* ───── Footer ───── */}
        <footer className="flex justify-end gap-3 border-t border-border px-7 py-4">
          <Button type="button" variant="ghost" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitDisabled}>
            {submitting ? "Creating…" : "Create agent"}
          </Button>
        </footer>
      </form>
    </Dialog>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-base font-semibold text-app-text">{title}</div>
      {hint && <div className="mb-2 mt-0.5 text-xs text-app-muted">{hint}</div>}
      {children}
    </div>
  )
}
